import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { SwastaDataType } from './types'
import type { FilterSwastaValues, MutationSwastaValues } from './validation'

const SWASTA_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['swasta', key] as const

const getAll = (
  params?: FilterSwastaValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<SwastaDataType>>> => ({
  queryKey: SWASTA_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<SwastaDataType>>
      >('/ketenagaan/penyuluh/swasta', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<SwastaDataType>> => ({
  queryKey: SWASTA_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<SwastaDataType>>(
        `/ketenagaan/penyuluh/swasta/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwastaDataType>,
  MutationSwastaValues
> => ({
  mutationFn: async (data: MutationSwastaValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<SwastaDataType>>(
        '/ketenagaan/penyuluh/swasta',
        {
          ...data,
          id_desa_penyuluh: data.lokasi_penyuluh.id_desa.value,
          id_desa_perusahaan: data.lokasi_perusahaan.id_desa.value,
          wilayah_kerja: data.wilayah_kerja.value,
          id_bidang_usaha: data.id_bidang_usaha.value,
          alamat_perusahaan: data.lokasi_perusahaan.alamat,
          alamat_penyuluh: data.lokasi_penyuluh.alamat,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwastaDataType>,
  MutationSwastaValues
> => ({
  mutationFn: async (data: MutationSwastaValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<SwastaDataType>>(
        `/ketenagaan/penyuluh/swasta/${data.id_swasta}`,
        {
          ...data,
          id_desa_penyuluh: data.lokasi_penyuluh.id_desa.value,
          id_desa_perusahaan: data.lokasi_perusahaan.id_desa.value,
          wilayah_kerja: data.wilayah_kerja.value,
          id_bidang_usaha: data.id_bidang_usaha.value,
          alamat_perusahaan: data.lokasi_perusahaan.alamat,
          alamat_penyuluh: data.lokasi_penyuluh.alamat,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwastaDataType>,
  { id: string }
> => ({
  mutationFn: async (data: { id: string }) => {
    try {
      const response = await api.delete<ServiceGetByIdResponse<SwastaDataType>>(
        `/ketenagaan/penyuluh/swasta/${data.id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (filterParams?: FilterSwastaValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/ketenagaan/penyuluh/swasta/export`, {
        params: {
          ...filterParams,
          jenis: 'swasta'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const swasta = {
  queryKey: [SWASTA_QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData,
  exportData
}
